import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Card,
  CardContent,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Button
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import surveyeditor from '../../assets/images/surveyeditor.webp';
import form from '../../assets/images/form.webp';
import analytics from '../../assets/images/analytics.webp';
import aisurvey from '../../assets/images/aisurvey.webp';
import ai from '../../assets/images/ai.webp';
import template from '../../assets/images/template.webp';

const CreateSurveyTutorial = () => {
  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        Creating a Survey: AI, Templates, and Scratch
      </Typography>
      <Box sx={{ width: 56, height: 4, bgcolor: 'primary.main', borderRadius: 2, mb: 2 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        SurveySlack offers three ways to start a new survey. Choose the method that works best for you.
      </Typography>

      {/* 1. AI */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>1. Starting with AI (Recommended)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Use the AI to instantly generate a complete survey draft based on your goal.</Typography>

       
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>1</Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Start the Creation Wizard</Typography>
                    <Typography variant="body2" color="text.secondary">From the Dashboard, click the "Create With AI" button.</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>2</Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>View Suggested Questions</Typography>
                    <Typography variant="body2" color="text.secondary">After clicking "Generate" you'll see a list of suggested questions. Each item has a checkbox so you can include or exclude it.</Typography>
                   
                     <Box
          component="img"
          src={ai}
          alt="Main Dashboard view with callouts for key sections"
          sx={{
            width: '100%',
            borderRadius: 1,
            display: 'block',
             boxShadow: (theme) => theme.shadows[3],
              mt: 3, 
          }}
        />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>3</Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Select Questions and Create</Typography>
                    <Typography variant="body2" color="text.secondary">Select the questions you want, then click the "Create Survey with Selected Questions" button at the bottom to proceed.</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">Clicking the button opens a modal where you enter a survey name before creating.</Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>4</Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Name Your Survey</Typography>
                    <Typography variant="body2" color="text.secondary">In the modal, enter a name for your survey and confirm. This creates the survey and opens the Survey Builder with your selected questions preloaded.</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>5</Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Open Builder & Edit</Typography>
                    <Typography variant="body2" color="text.secondary">The Survey Builder will open showing the selected questions. Review, edit, and publish when ready.</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>

        
       
      </Box>

      {/* 2. Templates */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>2. Starting with a Template</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Use a pre-built template for common use cases.</Typography>

       
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>1</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Start the Creation Wizard</Typography>
                <Typography variant="body2" color="text.secondary">Click "Browse Templates" from the create wizard to open the template library.</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>2</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Browse Template Library</Typography>
                <Typography variant="body2" color="text.secondary">Explore templates for common use cases. Click any template to preview details.</Typography>
                <Paper variant="outlined" sx={{ mt: 1, p: 2 }}>
                  <Typography variant="body2" color="text.secondary">Example Templates: Customer Satisfaction (CSAT), Employee Engagement, Event Feedback, Market Research</Typography>
                </Paper>
                <Box
          component="img"
          src={template}
          alt="Main Dashboard view with callouts for key sections"
          sx={{
            mt: 3,
            width: '100%',
            borderRadius: 1,
            display: 'block',
              boxShadow: (theme) => theme.shadows[3],
          }}
        />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>3</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Choose a Template & Name</Typography>
                <Typography variant="body2" color="text.secondary">Clicking a template opens a "Name your survey" modal. Enter a name and confirm to create.</Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">The modal ensures your survey gets a unique title before creation.</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>4</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Create & Open Builder</Typography>
                <Typography variant="body2" color="text.secondary">After confirming the name, the app creates the survey and opens the Survey Builder with the template content preloaded for editing.</Typography>
              </Box>
            </Box>
          </Grid>

       
        
      </Box>

      {/* 3. From Scratch */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>3. Starting from Scratch</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Build your survey question-by-question using the drag-and-drop editor.</Typography>

       
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>1</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Start the Creation Wizard</Typography>
                <Typography variant="body2" color="text.secondary">Click the "Create New Survey" button on the Dashboard. This opens a theme selection modal so you can pick a visual style.</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>2</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Select a Theme</Typography>
                <Typography variant="body2" color="text.secondary">Choose a survey theme (colors, spacing, fonts) in the modal and confirm. After selecting a theme, you'll be prompted to name your survey.</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>3</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Name Your Survey</Typography>
                <Typography variant="body2" color="text.secondary">Enter a title in the "Name your survey" modal and confirm. This creates the survey and opens the Survey Builder with an empty survey using the chosen theme.</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>4</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Open the Builder</Typography>
                <Typography variant="body2" color="text.secondary">The Survey Builder will open with the selected theme applied. Add questions, customize, and publish when ready.</Typography>
              </Box>
            </Box>
          </Grid>

        
       
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Choosing the Right Method table */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Choosing the Right Method</Typography>
      <Paper variant="outlined" sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Best For</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Time to Create</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>AI Creation</TableCell>
              <TableCell>Quick surveys with smart suggestions</TableCell>
              <TableCell>2-3 minutes</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Templates</TableCell>
              <TableCell>Common survey types with best practices</TableCell>
              <TableCell>3-5 minutes</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>From Scratch</TableCell>
              <TableCell>Custom surveys with full control</TableCell>
              <TableCell>10-20 minutes</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      {/* Bottom navigation CTA */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
      <Button
                variant="contained"
                color="inherit"
                size="medium"
                href="/app/documentations/getting-started/sign-up-dashboard"
              >
                Previous
              </Button>  
 <Button
                variant="contained"
                size="medium"
                href="/app/documentations/building-surveys/survey-builder-basics"
              >
                Next
              </Button>   
                 </Box>
    </Box>
  );
};

export default CreateSurveyTutorial;
         

